import React, { useState } from 'react';
import { Card, Form, Input, Select, Button, Upload, Space, message, Row, Col, Divider, Tag, Alert } from 'antd';
import { UploadOutlined, CloudUploadOutlined, ReloadOutlined, FileOutlined, InboxOutlined } from '@ant-design/icons';
import ReactJson from '@microlink/react-json-view';
import { getChannelLogo } from '../utils/channelLogos';
import { metaApi } from '../services/api';

const { Dragger } = Upload;

const CHANNELS = ['PYVIO', 'TAZAPAY'];

const SCENES = [
  { value: 'ACCOUNT_OPEN', label: '开户 (ACCOUNT_OPEN)' },
  { value: 'USER_CREATE', label: '用户创建 (USER_CREATE)' },
  { value: 'DEPOSIT', label: '充值 (DEPOSIT)' },
  { value: 'WITHDRAW', label: '提现 (WITHDRAW)' },
  { value: 'CARD_BIND', label: '绑卡 (CARD_BIND)' },
  { value: 'REFUND', label: '退款 (REFUND)' },
];

const DOCUMENT_TYPES_DEFAULT = [
  { group: 'KYC', items: [
    { value: 'ID_CARD', label: '身份证 (ID_CARD)' },
    { value: 'PASSPORT', label: '护照 (PASSPORT)' },
    { value: 'DRIVER_LICENSE', label: '驾照 (DRIVER_LICENSE)' },
    { value: 'ID_CARD_FRONT', label: '身份证正面 (ID_CARD_FRONT)' },
    { value: 'ID_CARD_BACK', label: '身份证背面 (ID_CARD_BACK)' },
    { value: 'SELFIE', label: '自拍照 (SELFIE)' },
  ]},
  { group: 'EDD/AML', items: [
    { value: 'ADDRESS_PROOF', label: '地址证明 (ADDRESS_PROOF)' },
    { value: 'BANK_STATEMENT', label: '银行流水 (BANK_STATEMENT)' },
    { value: 'SOURCE_OF_FUNDS', label: '资金来源 (SOURCE_OF_FUNDS)' },
    { value: 'SOURCE_OF_WEALTH', label: '财富来源 (SOURCE_OF_WEALTH)' },
  ]},
  { group: 'CARD', items: [
    { value: 'CARD_FRONT', label: '银行卡正面 (CARD_FRONT)' },
    { value: 'CARD_BACK', label: '银行卡背面 (CARD_BACK)' },
  ]},
  { group: 'OTHER', items: [
    { value: 'AUTHORIZATION_LETTER', label: '授权书 (AUTHORIZATION_LETTER)' },
  ]},
];

const DOCUMENT_TYPES_TAZAPAY = [
  { group: '企业文档 (Company)', items: [
    { value: 'registrationProofDoc', label: '注册证明 (registrationProofDoc)' },
    { value: 'AddressProofDoc', label: '地址证明 (AddressProofDoc)' },
    { value: 'beneficialOwnershipDoc', label: '受益所有权 (beneficialOwnershipDoc)' },
    { value: 'bankStatementProofDoc', label: '银行流水 (bankStatementProofDoc)' },
    { value: 'previousCardStatementProofDoc', label: '历史卡账单 (previousCardStatementProofDoc)' },
    { value: 'sampleInvoicesDoc', label: '样本发票 (sampleInvoicesDoc)' },
    { value: 'additionalDocs', label: '补充材料 (additionalDocs)' },
  ]},
  { group: '代表人文档 (Legal)', items: [
    { value: 'passport', label: '护照 (passport)' },
    { value: 'id_front', label: '身份证正面 (id_front)' },
    { value: 'id_back', label: '身份证背面 (id_back)' },
  ]},
];

const DOCUMENT_SIDES = [
  { value: 'FRONT', label: '正面 (FRONT)' },
  { value: 'BACK', label: '背面 (BACK)' },
];

export default function FileUpload() {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [fileList, setFileList] = useState([]);
  const [selectedChannel, setSelectedChannel] = useState(null);

  const handleSubmit = async (values) => {
    if (fileList.length === 0) {
      message.warning('请选择要上传的文件');
      return;
    }
    setLoading(true);
    try {
      const rand = () => Math.random().toString(36).slice(2, 8);
      const ts = Date.now();
      const formData = new FormData();
      formData.append('channelCode', values.channelCode);
      formData.append('scene', values.scene);
      formData.append('documentType', values.documentType);
      formData.append('bizId', `biz_${ts}_${rand()}`);
      formData.append('externalUserId', localStorage.getItem('uid') || localStorage.getItem('username') || 'test');
      formData.append('requestId', `req_${ts}_${rand()}`);
      if (values.documentCountry) formData.append('documentCountry', values.documentCountry);
      if (values.documentSide) formData.append('documentSide', values.documentSide);
      formData.append('file', fileList[0].originFileObj || fileList[0]);

      const { data } = await metaApi.uploadFile(formData);
      setResult(data);
      if (String(data?.code) === '200' || data?.code === 200) {
        message.success('文件上传成功');
        // 保存上传记录到 localStorage，供创建用户时选择
        const fileId = data?.data?.fileId;
        if (fileId) {
          const uploadedFiles = JSON.parse(localStorage.getItem('tazapay_uploaded_files') || '[]');
          uploadedFiles.unshift({
            fileId,
            documentType: values.documentType,
            fileName: fileList[0]?.name || fileList[0]?.originFileObj?.name || 'unknown',
            originalFileName: fileList[0]?.name || fileList[0]?.originFileObj?.name || 'unknown',
            // S3 object key 格式: {channel}/{scene}/{bizId}/{documentType}/{fileId}
            localFilePath: `${values.channelCode}/${values.scene}/${formData.get('bizId')}/${values.documentType}/${fileId}`,
            channelCode: values.channelCode,
            uploadTime: new Date().toLocaleString(),
          });
          // 最多保留 50 条
          localStorage.setItem('tazapay_uploaded_files', JSON.stringify(uploadedFiles.slice(0, 50)));
        }
      } else {
        message.warning(`上传失败: ${data?.msg || '未知错误'}`);
      }
    } catch (error) {
      console.error('File upload error:', error);
      message.error('上传失败: ' + (error.response?.data?.msg || error.message));
      setResult(error.response?.data || { error: error.message });
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    form.resetFields();
    setResult(null);
    setFileList([]);
    setSelectedChannel(null);
  };

  const uploadProps = {
    beforeUpload: () => false, // prevent auto upload
    fileList,
    onChange: ({ fileList: newFileList }) => setFileList(newFileList.slice(-1)), // single file
    maxCount: 1,
    accept: '.jpg,.jpeg,.png,.pdf,.doc,.docx',
  };

  // Build grouped document type options based on selected channel
  const docTypes = selectedChannel === 'TAZAPAY' ? DOCUMENT_TYPES_TAZAPAY : DOCUMENT_TYPES_DEFAULT;
  const docTypeOptions = docTypes.map(group => ({
    label: group.group,
    options: group.items,
  }));

  return (
    <div style={{ padding: '16px 24px', height: 'calc(100vh - 64px)', overflow: 'auto' }}>
      <div className="fade-in-up">
        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          padding: '12px 24px', borderRadius: 8, marginBottom: 16,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)'
        }}>
          <Space size={12}>
            <CloudUploadOutlined style={{ color: '#fff', fontSize: 20 }} />
            <span style={{ fontSize: 18, fontWeight: 600, color: '#fff' }}>渠道文件上传</span>
          </Space>
          <Button icon={<ReloadOutlined />} onClick={handleReset} type="text" style={{ color: '#fff' }}>重置</Button>
        </div>

        <Row gutter={16}>
          {/* Left: Form */}
          <Col xs={24} lg={12}>
            <Card className="modern-card" bodyStyle={{ padding: 24 }}>
              <Form form={form} layout="vertical" onFinish={handleSubmit}>

                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 16, color: '#374151' }}>📋 基础信息</div>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item label="渠道" name="channelCode" rules={[{ required: true, message: '请选择渠道' }]}>
                      <Select placeholder="选择渠道" onChange={v => { setSelectedChannel(v); form.setFieldValue('documentType', undefined); }}
                        options={CHANNELS.map(c => {
                          const logo = getChannelLogo(c);
                          return { label: <Space><span style={{ fontSize: 16 }}>{logo.emoji}</span><span>{logo.name}</span></Space>, value: c };
                        })}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="业务场景" name="scene" rules={[{ required: true, message: '请选择场景' }]}>
                      <Select placeholder="选择场景" options={SCENES} />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item label="文件类型" name="documentType" rules={[{ required: true, message: '请选择文件类型' }]}>
                      <Select placeholder="选择文件类型" options={docTypeOptions} />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="文件面" name="documentSide">
                      <Select placeholder="选择（可选）" allowClear options={DOCUMENT_SIDES} />
                    </Form.Item>
                  </Col>
                </Row>

                <Divider style={{ margin: '16px 0' }} />
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 16, color: '#374151' }}>🔗 业务关联</div>

                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item label="证件国家" name="documentCountry" tooltip="如 SG / HK / US">
                      <Input placeholder="可选，例如: SG" />
                    </Form.Item>
                  </Col>
                </Row>

                <Divider style={{ margin: '16px 0' }} />
                <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 16, color: '#374151' }}>📎 文件选择</div>

                <Form.Item>
                  <Dragger {...uploadProps}>
                    <p className="ant-upload-drag-icon"><InboxOutlined style={{ color: '#667eea', fontSize: 40 }} /></p>
                    <p className="ant-upload-text" style={{ fontSize: 14 }}>点击或拖拽文件到此区域</p>
                    <p className="ant-upload-hint" style={{ fontSize: 12, color: '#9ca3af' }}>
                      支持 JPG / PNG / PDF / DOC / DOCX，单个文件
                    </p>
                  </Dragger>
                </Form.Item>

                {selectedChannel && (
                  <Alert
                    type="info" showIcon style={{ marginBottom: 16, fontSize: 12 }}
                    message={selectedChannel === 'PYVIO'
                      ? 'Pyvio: 文件将上传至 S3 后通过 Pyvio API 分发。支持 KYC/EDD 文件类型。'
                      : 'Tazapay: 文件先获取 pre-signed URL，再 PUT 上传。支持 KYC/Entity 文档。'}
                  />
                )}

                <Button type="primary" htmlType="submit" icon={<CloudUploadOutlined />}
                  loading={loading} size="large" block className="primary-button"
                  style={{ height: 48 }} disabled={fileList.length === 0}>
                  {loading ? '上传中...' : '上传文件'}
                </Button>
              </Form>
            </Card>
          </Col>

          {/* Right: Result */}
          <Col xs={24} lg={12}>
            <Card className="modern-card" title="📋 上传结果" bodyStyle={{ padding: 24 }}>
              {result ? (
                <div className="json-viewer">
                  <ReactJson src={result} collapsed={false} theme="rjv-default"
                    displayDataTypes={false} displayObjectSize={false} enableClipboard />
                </div>
              ) : (
                <div style={{ padding: 60, textAlign: 'center', color: '#999', background: '#f9fafb',
                  borderRadius: 8, border: '2px dashed #d1d5db' }}>
                  <FileOutlined style={{ fontSize: 48, marginBottom: 16, color: '#d1d5db' }} />
                  <div>提交表单后将显示上传结果</div>
                </div>
              )}
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
}
